import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import streamApi from '../../services/streamApi';
import { authApi, clearStoredAuth } from '../../services/authApi';
import { ROUTES } from '../../constants';
import { useNavbarUser } from '../../utils/navbarUser';
import './GoLivePage.css';

const LIVE_HEARTBEAT_INTERVAL_MS = 60000;
const LIVE_STATUS_POLL_INTERVAL_MS = 5000;
const SUBMISSIONS_POLL_INTERVAL_MS = 15000;
const LIVE_ADS_POLL_INTERVAL_MS = 5000;

function scheduleValueFromSubmission(submission) {
  const explicitChoice = String(submission?.schedule_choice || '').trim().toLowerCase();
  if (explicitChoice) {
    return explicitChoice;
  }

  const fallbackOffset = Number.parseInt(submission?.start_offset_minutes, 10);
  if (Number.isFinite(fallbackOffset) && fallbackOffset > 0) {
    return String(fallbackOffset);
  }

  return '';
}

function isConsumedSubmission(submission) {
  if (!submission) {
    return false;
  }

  if (submission.is_consumed || submission.queue_status === 'consumed') {
    return true;
  }

  const playbackStatus = String(submission.playback_status || '').trim().toLowerCase();
  if (playbackStatus === 'completed') {
    return true;
  }

  const remainingSeconds = Number.parseInt(submission.remaining_duration_seconds, 10);
  return Number.isFinite(remainingSeconds) && remainingSeconds <= 0;
}

/**
 * Go Live page — streamer-only controls.
 *
 * Auto-connects to the PolarAce ad-push-server backend to manage:
 * - Stream overlay preview with live ad rotation
 * - Overlay settings (position, size, theme)
 * - Pricing configuration (spade rate, gif multiplier)
 * - Submission queue (approve/reject fan submissions)
 */
function GoLivePage() {
  const navigate = useNavigate();
  const user = useNavbarUser({ notificationCount: 999 });
  const streamConfigError = streamApi.getConfigurationError();
  const isStreamConfigured = !streamConfigError;

  // ── Stream server connection state ──
  const [isConnected, setIsConnected] = useState(!!streamApi.getStreamToken());
  const [connecting, setConnecting] = useState(false);

  // ── Stream state ──
  const [isLive, setIsLive] = useState(false);
  const [liveStatusLoading, setLiveStatusLoading] = useState(false);
  const [liveActionPending, setLiveActionPending] = useState(false);
  const [liveSyncError, setLiveSyncError] = useState('');
  const [hasTwitchConnected, setHasTwitchConnected] = useState(false);
  const [twitchCheckComplete, setTwitchCheckComplete] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [queueFilter, setQueueFilter] = useState('all');
  const [overlaySettings, setOverlaySettings] = useState({
    position: 'bottom-right',
    size: 'medium',
    theme: 'glass',
    margin: 24,
  });
  const [pricing, setPricing] = useState({
    spade_rate: 200,
    gif_rate: 300,
  });

  // ── OBS URL copy state ──
  const [obsCopied, setObsCopied] = useState(false);
  const [obsUserCode, setObsUserCode] = useState('');
  const [obsApprovePending, setObsApprovePending] = useState(false);
  const [obsApproveMessage, setObsApproveMessage] = useState('');
  const [obsApproveError, setObsApproveError] = useState('');

  // ── Per-submission schedule (component-state only) ──
  const [scheduleMap, setScheduleMap] = useState({});

  // ── Overlay ad rotation state ──
  const [ads, setAds] = useState([]);
  const [currentAd, setCurrentAd] = useState(null);
  const [adVisible, setAdVisible] = useState(false);
  const [adProgress, setAdProgress] = useState(100);
  const [adTimeLeft, setAdTimeLeft] = useState(0);
  const adTimerRef = useRef(null);
  const adRotationRef = useRef(null);
  const streamStartRef = useRef(null);
  const twitchReconcileAttemptedRef = useRef(false);
  const liveStartedAtRef = useRef('');
  const currentAdRef = useRef(null);
  const playbackRequestRef = useRef({
    start: new Set(),
    complete: new Set(),
  });

  const applyLiveStatus = useCallback((liveStatus = {}) => {
    const nextIsLive = Boolean(liveStatus?.is_live);
    setIsLive(nextIsLive);

    if (nextIsLive) {
      const startedAt = Date.parse(liveStatus?.started_at || '');
      streamStartRef.current = Number.isNaN(startedAt) ? Date.now() : startedAt;
      liveStartedAtRef.current = String(liveStatus?.started_at || '').trim();
      return;
    }

    streamStartRef.current = null;
    liveStartedAtRef.current = '';
    setCurrentAd(null);
    setAdVisible(false);
  }, []);

  useEffect(() => {
    currentAdRef.current = currentAd;
  }, [currentAd]);

  const loadLiveStatus = useCallback(async ({ silent = true } = {}) => {
    if (!isConnected) {
      return null;
    }

    if (!silent) {
      setLiveStatusLoading(true);
    }

    try {
      const data = await streamApi.getLiveStatus();
      applyLiveStatus(data.live_status || {});
      setLiveSyncError('');
      return data.live_status || {};
    } catch (err) {
      if (!silent) {
        setLiveSyncError(err.message || 'Failed to load stream status.');
      }
      return null;
    } finally {
      if (!silent) {
        setLiveStatusLoading(false);
      }
    }
  }, [applyLiveStatus, isConnected]);

  // ── Data Loading ──
  const loadSubmissions = useCallback(async () => {
    try {
      const data = await streamApi.getSubmissions();
      const items = Array.isArray(data) ? data : data.submissions || [];
      setSubmissions(items);
      setScheduleMap((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (item?.id) {
            next[item.id] = scheduleValueFromSubmission(item);
          }
        });
        return next;
      });
    } catch {
      // Silently fail — will retry
    }
  }, []);

  const handleDisconnect = useCallback(() => {
    stopAdRotation();
    setIsLive(false);
    setLiveStatusLoading(false);
    setLiveActionPending(false);
    setLiveSyncError('');
    streamApi.clearStreamAuth();
    setIsConnected(false);
    setAds([]);
    setSubmissions([]);
    setCurrentAd(null);
    setAdVisible(false);
    streamStartRef.current = null;
  }, []);

  const loadAds = useCallback(async () => {
    try {
      const streamUser = streamApi.getStreamUser();
      if (!streamUser) return;

      const data = await streamApi.getAdsForOverlay(streamUser.id);
      setAds(data.images || []);

      if (data.overlay_settings) {
        setOverlaySettings((prev) => ({ ...prev, ...data.overlay_settings }));
      }
    } catch (err) {
      if (err.message.includes('expired')) {
        handleDisconnect();
      }
    }
  }, [handleDisconnect]);

  // ── Auto-connect on mount ──
  useEffect(() => {
    if (!isStreamConfigured || isConnected) {
      setConnecting(false);
      return;
    }
    setConnecting(true);
    streamApi.autoConnect('streamer').then((ok) => {
      setIsConnected(ok);
      setConnecting(false);
    });
  }, [isConnected, isStreamConfigured]);

  // ── If connected, load data ──
  useEffect(() => {
    if (isConnected) {
      loadSubmissions();
      loadAds();
      loadLiveStatus({ silent: false });
    }
  }, [isConnected, loadAds, loadLiveStatus, loadSubmissions]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    let cancelled = false;
    authApi
      .getProfile()
      .then((data) => {
        if (cancelled) return;
        const profile = data?.profile || {};
        const oauthIsTwitch = String(profile.oauthProvider || '').toLowerCase() === 'twitch'
          && String(profile.oauthProviderUsername || '').trim();
        const platforms = profile.connectedPlatforms || [];
        const platformHasTwitch = platforms.some(
          (p) => String(p?.platform || '').toLowerCase() === 'twitch'
            && String(p?.platformUsername || '').trim(),
        );
        setHasTwitchConnected(Boolean(oauthIsTwitch || platformHasTwitch));
      })
      .catch(() => {
        if (!cancelled) setHasTwitchConnected(false);
      })
      .finally(() => {
        if (!cancelled) setTwitchCheckComplete(true);
      });
    return () => { cancelled = true; };
  }, [isConnected]);

  useEffect(() => {
    if (twitchReconcileAttemptedRef.current) {
      return undefined;
    }

    twitchReconcileAttemptedRef.current = true;
    let cancelled = false;

    const reconcileTwitchSync = async () => {
      try {
        await authApi.reconcileTwitchEventSub();
        if (!cancelled) {
          loadLiveStatus();
        }
      } catch (err) {
        if ((err?.message || '').includes('Linked Twitch account not found')) {
          return;
        }
        console.warn('Twitch EventSub reconcile skipped:', err?.message || err);
      }
    };

    reconcileTwitchSync();

    return () => {
      cancelled = true;
    };
  }, [loadLiveStatus]);

  // ── Faster live-status refresh ──
  useEffect(() => {
    if (!isConnected) return undefined;

    const interval = setInterval(() => {
      loadLiveStatus();
    }, LIVE_STATUS_POLL_INTERVAL_MS);

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        loadLiveStatus();
        loadSubmissions();
        if (isLive) {
          loadAds();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [isLive, isConnected, loadAds, loadLiveStatus, loadSubmissions]);

  // ── Queue refresh ──
  useEffect(() => {
    if (!isConnected) return undefined;

    const interval = setInterval(() => {
      loadSubmissions();
    }, SUBMISSIONS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isConnected, loadSubmissions]);

  // ── Ads refresh while live ──
  useEffect(() => {
    if (!isConnected || !isLive) return undefined;

    const interval = setInterval(() => {
      loadAds();
    }, LIVE_ADS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isConnected, isLive, loadAds]);

  useEffect(() => {
    if (!isConnected || !isLive) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const data = await streamApi.heartbeatLiveStream();
        applyLiveStatus(data.live_status || {});
        setLiveSyncError('');
      } catch (err) {
        setLiveSyncError(err.message || 'Failed to keep stream live.');
      }
    }, LIVE_HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [applyLiveStatus, isConnected, isLive]);

  // ── Go Live ──
  const handleGoLive = async () => {
    setLiveActionPending(true);
    setLiveSyncError('');

    try {
      const data = await streamApi.startLiveStream();
      applyLiveStatus(data.live_status || { is_live: true });
      await loadAds();
    } catch (err) {
      setLiveSyncError(err.message || 'Failed to go live.');
    } finally {
      setLiveActionPending(false);
    }
  };

  const handleEndStream = async () => {
    setLiveActionPending(true);
    setLiveSyncError('');

    try {
      const data = await streamApi.stopLiveStream();
      applyLiveStatus(data.live_status || { is_live: false });
      stopAdRotation();
    } catch (err) {
      setLiveSyncError(err.message || 'Failed to end stream.');
    } finally {
      setLiveActionPending(false);
    }
  };

  const handleApproveObsCode = async () => {
    const normalizedUserCode = typeof obsUserCode === 'string'
      ? obsUserCode.trim().toUpperCase()
      : '';

    if (!normalizedUserCode) {
      setObsApproveError('Enter the OBS user code shown in the plugin first.');
      setObsApproveMessage('');
      return;
    }

    setObsApprovePending(true);
    setObsApproveError('');
    setObsApproveMessage('');

    try {
      await authApi.approveObsDeviceCode(normalizedUserCode);
      setObsUserCode(normalizedUserCode);
      setObsApproveMessage('OBS login approved. Go back to OBS and click Check device approval.');
    } catch (err) {
      setObsApproveError(err.message || 'Failed to approve OBS user code.');
    } finally {
      setObsApprovePending(false);
    }
  };

  const stopAdRotation = useCallback(({ clearCurrent = true } = {}) => {
    clearTimeout(adTimerRef.current);
    clearTimeout(adRotationRef.current);

    if (clearCurrent) {
      setCurrentAd(null);
      setAdVisible(false);
      setAdProgress(100);
      setAdTimeLeft(0);
      currentAdRef.current = null;
    }
  }, []);

  const completePreviewPlayback = useCallback(async (submissionId, displayedSeconds) => {
    if (!submissionId) {
      stopAdRotation();
      return;
    }

    const pendingCompletes = playbackRequestRef.current.complete;
    if (pendingCompletes.has(submissionId)) {
      return;
    }

    pendingCompletes.add(submissionId);
    try {
      await streamApi.updateSubmissionPlayback(submissionId, {
        playback_event: 'progress',
        displayed_seconds: displayedSeconds,
      });
    } catch (err) {
      console.error('Preview playback progress failed:', err.message);
    } finally {
      pendingCompletes.delete(submissionId);
      await loadAds();
    }
  }, [loadAds, stopAdRotation]);

  const showAd = useCallback(async (ad) => {
    const submissionId = String(ad?.submission_id || '').trim();
    if (!submissionId) {
      stopAdRotation();
      return;
    }

    const pendingStarts = playbackRequestRef.current.start;
    if (ad.playback_status !== 'active' && !pendingStarts.has(submissionId)) {
      pendingStarts.add(submissionId);
      try {
        await streamApi.updateSubmissionPlayback(submissionId, {
          playback_event: 'start',
          activated_stream_started_at: liveStartedAtRef.current,
        });
      } catch (err) {
        console.error('Preview playback start failed:', err.message);
      } finally {
        pendingStarts.delete(submissionId);
      }
    }

    stopAdRotation({ clearCurrent: false });

    const duration = Math.max(
      1,
      Number.parseInt(ad.duration || ad.remaining_duration_seconds || 30, 10) || 30,
    );

    setCurrentAd(ad);
    currentAdRef.current = ad;
    setAdProgress(100);
    setAdTimeLeft(duration);
    setAdVisible(false);

    setTimeout(() => setAdVisible(true), 100);

    let remaining = duration;
    const countdownTick = () => {
      remaining = Math.max(0, remaining - 1);
      setAdTimeLeft(remaining);
      setAdProgress((remaining / duration) * 100);

      if (remaining > 0) {
        adTimerRef.current = setTimeout(countdownTick, 1000);
      }
    };
    adTimerRef.current = setTimeout(countdownTick, 1000);

    adRotationRef.current = setTimeout(() => {
      setAdVisible(false);
      setTimeout(() => {
        completePreviewPlayback(submissionId, duration);
      }, 600);
    }, duration * 1000);
  }, [completePreviewPlayback, stopAdRotation]);

  useEffect(() => {
    if (!isLive) {
      stopAdRotation();
      return undefined;
    }

    if (ads.length === 0) {
      stopAdRotation();
      return undefined;
    }

    const activeAd = ads.find((item) => item.playback_status === 'active') || null;
    const nextAd = activeAd || ads[0] || null;
    const currentSubmissionId = String(currentAdRef.current?.submission_id || '').trim();

    if (activeAd) {
      const activeSubmissionId = String(activeAd.submission_id || '').trim();
      if (currentSubmissionId !== activeSubmissionId) {
        showAd(activeAd);
      }
      return undefined;
    }

    if (currentSubmissionId) {
      const currentStillEligible = ads.some(
        (item) => String(item.submission_id || '').trim() === currentSubmissionId,
      );
      if (currentStillEligible) {
        return undefined;
      }
    }

    if (nextAd) {
      showAd(nextAd);
    } else {
      stopAdRotation();
    }

    return undefined;
  }, [ads, isLive, showAd, stopAdRotation]);

  // ── Settings Updates ──
  const handleOverlayChange = async (key, value) => {
    const updated = { ...overlaySettings, [key]: value };
    setOverlaySettings(updated);

    try {
      await streamApi.updateSettings({ overlay_settings: updated });
    } catch {
      // Revert silently
    }
  };

  const handlePricingChange = async (key, value) => {
    const numValue = parseInt(value) || 0;
    const updated = { ...pricing, [key]: numValue };
    setPricing(updated);

    try {
      await streamApi.updateSettings({ pricing: updated });
    } catch {
      // Revert silently
    }
  };

  // ── Submission Actions ──
  const handleApprove = async (id) => {
    try {
      await streamApi.reviewSubmission(id, 'approved');
      loadSubmissions();
      setTimeout(loadAds, 500);
    } catch (err) {
      console.error('Approve failed:', err.message);
    }
  };

  const handleReject = async (id) => {
    try {
      await streamApi.reviewSubmission(id, 'rejected');
      loadSubmissions();
    } catch (err) {
      console.error('Reject failed:', err.message);
    }
  };

  const handleToggleShow = async (id) => {
    try {
      await streamApi.toggleShowOnStream(id);
      loadSubmissions();
      setTimeout(loadAds, 500);
    } catch (err) {
      console.error('Toggle failed:', err.message);
    }
  };

  const handleScheduleChange = async (submissionId, nextSchedule) => {
    const previousSchedule = scheduleMap[submissionId] || '';

    setScheduleMap((prev) => ({
      ...prev,
      [submissionId]: nextSchedule,
    }));

    try {
      await streamApi.updateSubmissionSchedule(submissionId, nextSchedule);
      await loadSubmissions();
      if (isLive) {
        setTimeout(loadAds, 500);
      }
    } catch (err) {
      setScheduleMap((prev) => ({
        ...prev,
        [submissionId]: previousSchedule,
      }));
      console.error('Schedule update failed:', err.message);
    }
  };

  // ── Logout (frontend) ──
  const handleLogout = useCallback(async () => {
    await authApi.logout();
    clearStoredAuth();
    handleDisconnect();
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  // ── Filter submissions ──
  const filteredSubmissions =
    queueFilter === 'all'
      ? submissions
      : submissions.filter((s) => {
          if (queueFilter === 'approved') {
            return s.status === 'approved' && !isConsumedSubmission(s);
          }
          return s.status === queueFilter;
        });

  // ── Timer display ──
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="go-live-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        minimal
      />

      <LeftSidebar />

      <main className="go-live-page__main">
        <div className="go-live-page__content">
          {/* Page Header */}
          <div className="go-live-page__header">
            <h1 className="go-live-page__title">Go Live!</h1>
            {isConnected && (
              <div
                className={`go-live-page__status go-live-page__status--${isLive ? 'live' : 'offline'}`}
              >
                <span className="go-live-page__status-dot" />
                {isLive ? 'LIVE' : 'Offline'}
              </div>
            )}
          </div>

          {connecting && (
            <div className="go-live-page__connecting">Connecting to stream server...</div>
          )}

          {liveStatusLoading && !connecting && (
            <div className="go-live-page__connecting">Syncing live status...</div>
          )}

          {streamConfigError && (
            <div className="go-live-page__warning">
              {streamConfigError}
            </div>
          )}

          {liveSyncError && (
            <div className="go-live-page__warning">
              {liveSyncError}
            </div>
          )}

          {isConnected && twitchCheckComplete && !hasTwitchConnected && !isLive && (
            <div className="go-live-page__warning">
              Connect a Twitch account on your profile to go live. Your stream starts
              automatically when you go live on Twitch.
            </div>
          )}

          {/* Dashboard Grid: Preview + Controls */}
          <div className="go-live-page__dashboard">
            {/* Stream Preview */}
            <div className="go-live-page__preview">
              {!isLive ? (
                <div className="go-live-page__preview-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 7l-7 5 7 5V7z" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  <span>Stream preview will appear when you go live</span>
                </div>
              ) : (
                <>
                  <div className="go-live-page__preview-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    <span>Camera feed</span>
                  </div>

                  {/* Live Badge */}
                  <div className="go-live-page__preview-live-badge">
                    <span />
                    LIVE
                  </div>

                  {/* Overlay Ad Card */}
                  {currentAd && (
                    <div
                      className={`go-live-page__overlay-anchor go-live-page__overlay-anchor--${overlaySettings.position}`}
                    >
                      <div
                        className={`go-live-page__overlay-card go-live-page__overlay-card--${overlaySettings.theme} ${adVisible ? 'go-live-page__overlay-card--visible' : ''}`}
                      >
                        <div className="go-live-page__overlay-image">
                          <img src={currentAd.url} alt={currentAd.title || ''} />
                        </div>
                        <div className="go-live-page__overlay-info">
                          <div className="go-live-page__overlay-title">
                            {currentAd.title || 'Untitled'}
                          </div>
                          <div className="go-live-page__overlay-meta">
                            <span className="go-live-page__overlay-fan">
                              {(currentAd.fan_username || currentAd.fan_id)
                                ? `by ${currentAd.fan_username || currentAd.fan_id}`
                                : ''}
                            </span>
                            <span className="go-live-page__overlay-timer">
                              {formatTime(adTimeLeft)}
                            </span>
                          </div>
                        </div>
                        <div className="go-live-page__overlay-progress">
                          <div
                            className="go-live-page__overlay-progress-fill"
                            style={{ width: `${adProgress}%` }}
                          />
                        </div>
                        <div className="go-live-page__overlay-brand">PolarAce</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Controls Panel */}
            <div className="go-live-page__controls">
              {/* Go Live / End Stream */}
              <button
                type="button"
                className={`go-live-page__go-live-btn go-live-page__go-live-btn--${isLive ? 'stop' : 'start'}`}
                onClick={isLive ? handleEndStream : handleGoLive}
                disabled={
                  liveActionPending
                  || liveStatusLoading
                  || (!isLive && !isStreamConfigured)
                  || (!isLive && twitchCheckComplete && !hasTwitchConnected)
                }
                title={
                  !isLive && twitchCheckComplete && !hasTwitchConnected
                    ? 'Connect a Twitch account on your profile to go live'
                    : undefined
                }
              >
                {liveActionPending ? (
                  isLive ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="6" width="12" height="12" rx="2" />
                      </svg>
                      Ending...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="2" />
                        <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
                        <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                        <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
                      </svg>
                      Starting...
                    </>
                  )
                ) : isLive ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                    End Stream
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="2" />
                      <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
                      <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
                    </svg>
                    Go Live!
                  </>
                )}
              </button>

              {/* Overlay Settings */}
              <div className="go-live-page__card">
                <h3 className="go-live-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                  Overlay Settings
                </h3>

                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">Position</span>
                  <select
                    className="go-live-page__form-select"
                    value={overlaySettings.position}
                    onChange={(e) => handleOverlayChange('position', e.target.value)}
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                  </select>
                </div>

                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">Theme</span>
                  <select
                    className="go-live-page__form-select"
                    value={overlaySettings.theme}
                    onChange={(e) => handleOverlayChange('theme', e.target.value)}
                  >
                    <option value="glass">Glass</option>
                    <option value="solid">Solid</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
              </div>

              {/* Pricing */}
              <div className="go-live-page__card">
                <h3 className="go-live-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  Pricing
                </h3>

                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">{'\u2660'} Image Rate (/min)</span>
                  <input
                    type="number"
                    className="go-live-page__form-input"
                    value={pricing.spade_rate}
                    onChange={(e) => handlePricingChange('spade_rate', e.target.value)}
                    min="1"
                  />
                </div>

                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">{'\u2660'} GIF Rate (/min)</span>
                  <input
                    type="number"
                    className="go-live-page__form-input"
                    value={pricing.gif_rate}
                    onChange={(e) => handlePricingChange('gif_rate', e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              {/* Info Card */}
              <div className="go-live-page__card">
                <h3 className="go-live-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Stream Info
                </h3>
                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">Queued Ads</span>
                  <span className="go-live-page__metric go-live-page__metric--violet">
                    {ads.length}
                  </span>
                </div>
                <div className="go-live-page__form-row">
                  <span className="go-live-page__form-label">Pending Submissions</span>
                  <span className="go-live-page__metric go-live-page__metric--amber">
                    {submissions.filter((s) => s.status === 'pending').length}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* OBS Browser Source */}
          {isConnected && (
            <div className="go-live-page__obs-card">
              <h3 className="go-live-page__card-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                OBS Browser Source
              </h3>
              <p className="go-live-page__obs-description">
                Add this URL as a Browser Source in OBS to display ads on your stream.
              </p>
              <div className="go-live-page__obs-url-container">
                <input
                  type="text"
                  className="go-live-page__obs-url"
                  value={streamApi.getOverlayUrl(overlaySettings) || ''}
                  readOnly
                />
                <button
                  type="button"
                  className="go-live-page__obs-copy-btn"
                  onClick={() => {
                    const url = streamApi.getOverlayUrl(overlaySettings);
                    if (url) {
                      navigator.clipboard.writeText(url);
                      setObsCopied(true);
                      setTimeout(() => setObsCopied(false), 2000);
                    }
                  }}
                >
                  {obsCopied ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
              <ol className="go-live-page__obs-instructions">
                <li>Open OBS Studio and go to your scene</li>
                <li>Click the + button under Sources and select "Browser"</li>
                <li>Paste the URL above into the URL field</li>
                <li>Set width to 1920 and height to 1080</li>
                <li>Click OK — ads will appear automatically when approved</li>
              </ol>

              <div className="go-live-page__obs-native">
                <h4 className="go-live-page__obs-native-title">OBS Native Extension Approval</h4>
                <p className="go-live-page__obs-description">
                  If the native OBS plugin shows a user code, enter it here to approve the login without using browser DevTools.
                </p>
                <div className="go-live-page__obs-approve-row">
                  <input
                    type="text"
                    className="go-live-page__obs-code-input"
                    value={obsUserCode}
                    onChange={(e) => {
                      setObsUserCode(e.target.value.toUpperCase());
                      if (obsApproveError) setObsApproveError('');
                      if (obsApproveMessage) setObsApproveMessage('');
                    }}
                    placeholder="ABCD-1234"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="go-live-page__obs-copy-btn"
                    onClick={handleApproveObsCode}
                    disabled={obsApprovePending}
                  >
                    {obsApprovePending ? 'Approving...' : 'Approve Code'}
                  </button>
                </div>
                {obsApproveMessage && (
                  <p className="go-live-page__obs-status go-live-page__obs-status--success">
                    {obsApproveMessage}
                  </p>
                )}
                {obsApproveError && (
                  <p className="go-live-page__obs-status go-live-page__obs-status--error">
                    {obsApproveError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Submission Queue */}
          <div className="go-live-page__queue">
            <div className="go-live-page__queue-header">
              <h2 className="go-live-page__queue-title">
                Submission Queue
                {submissions.length > 0 && (
                  <span className="go-live-page__queue-badge">{submissions.length}</span>
                )}
              </h2>

              <div className="go-live-page__queue-filters">
                {['all', 'pending', 'approved', 'rejected'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`go-live-page__queue-filter ${queueFilter === f ? 'go-live-page__queue-filter--active' : ''}`}
                    onClick={() => setQueueFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {filteredSubmissions.length === 0 ? (
              <div className="go-live-page__queue-empty">
                {submissions.length === 0
                  ? 'No submissions yet. Fans can submit media to display on your stream.'
                  : `No ${queueFilter} submissions.`}
              </div>
            ) : (
              <div className="go-live-page__sub-grid">
                {filteredSubmissions.map((sub) => {
                  const thumbSource = sub.image_preview_url || sub.media_image_url || sub.image_url || '';
                  const thumbUrl = thumbSource
                    ? streamApi.resolveImageUrl(thumbSource)
                    : null;
                  const isGif = sub.media_type === 'gif';
                  const isConsumed = isConsumedSubmission(sub);
                  const statusLabel = isConsumed ? 'consumed' : sub.status;

                  return (
                    <div
                      key={sub.id}
                      className={`go-live-page__sub-card go-live-page__sub-card--${sub.status} ${isConsumed ? 'go-live-page__sub-card--consumed' : ''}`}
                    >
                      {/* Thumbnail */}
                      <div className="go-live-page__sub-thumb">
                        {thumbUrl ? (
                          <img src={thumbUrl} alt={sub.media_title || 'Submission'} />
                        ) : (
                          <div className="go-live-page__sub-thumb-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                        )}
                        {isGif && <span className="go-live-page__sub-gif-badge">GIF</span>}

                        {/* Status badge */}
                        <span className={`go-live-page__sub-status go-live-page__sub-status--${statusLabel}`}>
                          {statusLabel}
                        </span>

                        {/* Pending overlay with actions */}
                        {sub.status === 'pending' && (
                          <div className="go-live-page__sub-overlay">
                            <button
                              type="button"
                              className="go-live-page__queue-action go-live-page__queue-action--approve"
                              onClick={() => handleApprove(sub.id)}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="go-live-page__queue-action go-live-page__queue-action--reject"
                              onClick={() => handleReject(sub.id)}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="go-live-page__sub-info">
                        <div className="go-live-page__sub-title">
                          {sub.media_title || sub.media_id?.slice(0, 12) || 'Untitled'}
                        </div>
                        <div className="go-live-page__sub-meta">
                          <span className="go-live-page__sub-fan">{sub.fan_username || sub.fan_id}</span>
                          <span className="go-live-page__sub-cost">
                            {'\u2660'} {sub.spades_cost || '-'}
                          </span>
                        </div>
                        <div className="go-live-page__sub-meta">
                          <span className="go-live-page__sub-duration">
                            {sub.duration_minutes || 10} min
                          </span>
                          {isConsumed && (
                            <span className="go-live-page__sub-consumed-note">
                              Fully used
                            </span>
                          )}
                        </div>

                        {/* Approved: Show/Hide + Schedule */}
                        {sub.status === 'approved' && (
                          <div className="go-live-page__sub-approved-actions">
                            <button
                              type="button"
                              className="go-live-page__queue-action go-live-page__queue-action--toggle"
                              onClick={() => handleToggleShow(sub.id)}
                              disabled={isConsumed}
                            >
                              {isConsumed ? 'Consumed' : (sub.show_on_stream ? 'Hide' : 'Show')}
                            </button>
                            <select
                              className="go-live-page__sub-schedule"
                              value={scheduleMap[sub.id] || ''}
                              onChange={(e) => handleScheduleChange(sub.id, e.target.value)}
                              disabled={isConsumed}
                            >
                              <option value="">Schedule...</option>
                              <option value="start">Show at start</option>
                              <option value="3">After 3 min</option>
                              <option value="5">After 5 min</option>
                              <option value="10">After 10 min</option>
                              <option value="15">After 15 min</option>
                              <option value="30">After 30 min</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
}

export default GoLivePage;
